import { Action } from 'redux';

import {
  AxiosAction,
  isSuccessActionOf
} from '../middleware/axios';
import { isAction } from '../utils';

import {
  LibraryDetailedResponse,
  LibraryDto,
  LibraryListingResponse,
  LibraryPublishResponse
} from './state';

interface GetLibrariesParams {
  filter?: string;
  skip?: number;
  take?: number;
}

// Action Types

const GET_LIBRARY = 'LIBRARIES.GET';
const GET_LIBRARIES = 'LIBRARIES.GET_ALL';
const GET_LIBRARY_COUNT = 'LIBRARIES.COUNT';
const CREATE_LIBRARY = 'LIBRARIES.CREATE';
const UPDATE_LIBRARY = 'LIBRARIES.UPDATE';
const PUBLISH_LIBRARY = 'LIBRARY.PUBLISH';
const CLEAR_RECENTLY_MODIFIED = 'LIBRARIES.CLEAR_RECENTLY_MODIFIED';

// Action Interfaces

export interface GetLibrary extends AxiosAction<LibraryDetailedResponse> {
  type: typeof GET_LIBRARY;
}
export interface GetLibraries extends AxiosAction<LibraryListingResponse[]> {
  type: typeof GET_LIBRARIES;
}
export interface GetLibraryCount extends AxiosAction<{ count: number }> {
  type: typeof GET_LIBRARY_COUNT;
}
export interface CreateLibrary extends AxiosAction<LibraryDetailedResponse> {
  type: typeof CREATE_LIBRARY;
}
export interface UpdateLibrary extends AxiosAction<LibraryDetailedResponse> {
  code: string;
  type: typeof UPDATE_LIBRARY;
}
export interface PublishLibrary extends AxiosAction<LibraryPublishResponse> {
  code: string;
  type: typeof PUBLISH_LIBRARY;
}
export interface ClearRecentlyModified extends Action {
  type: typeof CLEAR_RECENTLY_MODIFIED;
}

// Action Creators

/** Fetches a single library model's data by code. */
export function getLibrary(code: string): GetLibrary {
  return {
    payload: {
      client: 'coreService',
      request: {
        method: 'get',
        url: `/libraries/${code}`
      }
    },
    type: GET_LIBRARY
  };
}
export const isGetLibrary = isAction<GetLibrary>(GET_LIBRARY);
export const isGetLibrarySuccess = isSuccessActionOf<GetLibrary>(GET_LIBRARY);

/** Gets all libraries available to the current user. */
export function getLibraries(params: GetLibrariesParams): GetLibraries {
  return {
    payload: {
      client: 'coreService',
      request: {
        params,
        url: '/libraries'
      }
    },
    type: GET_LIBRARIES
  };
}
export const isGetLibraries = isAction<GetLibraries>(GET_LIBRARIES);
export const isGetLibrariesSuccess = isSuccessActionOf<GetLibraries>(GET_LIBRARIES);

/** Get library count for logged in use */
export function getLibraryCount(params: GetLibrariesParams): GetLibraryCount {
  return {
    payload: {
      client: 'coreService',
      request: {
        params,
        url: '/libraries/count'
      }
    },
    type: GET_LIBRARY_COUNT
  };
}
export const isGetLibraryCount = isAction<GetLibraryCount>(GET_LIBRARY_COUNT);
export const isGetLibraryCountSuccess = isSuccessActionOf<GetLibraryCount>(GET_LIBRARY_COUNT);

/** Creates a library with the given contents. */
export function createLibrary(data: LibraryDto): CreateLibrary {
  return {
    payload: {
      client: 'coreService',
      request: {
        data,
        method: 'POST',
        url: '/libraries'
      }
    },
    type: CREATE_LIBRARY
  };
}
export const isCreateLibrary = isAction<CreateLibrary>(CREATE_LIBRARY);
export const isCreateLibrarySuccess = isSuccessActionOf<CreateLibrary>(CREATE_LIBRARY);

/** Updates the library with the given code to have the given contents. */
export function updateLibrary(code: string, data: LibraryDto): UpdateLibrary {
  return {
    code,
    payload: {
      client: 'coreService',
      request: {
        data,
        method: 'PUT',
        url: `/libraries/${code}`
      }
    },
    type: UPDATE_LIBRARY
  };
}
export const isUpdateLibrary = isAction<UpdateLibrary>(UPDATE_LIBRARY);
export const isUpdateLibrarySuccess = isSuccessActionOf<UpdateLibrary>(UPDATE_LIBRARY);

/** Publishes the library with the given code. */
export function publishLibrary(code: string): PublishLibrary {
  return {
    code,
    payload: {
      client: 'coreService',
      request: {
        method: 'POST',
        url: `/libraries/${code}/publish`
      }
    },
    type: PUBLISH_LIBRARY
  };
}
export const isPublishLibrary = isAction<PublishLibrary>(PUBLISH_LIBRARY);
export const isPublishLibrarySuccess = isSuccessActionOf<PublishLibrary>(PUBLISH_LIBRARY);

/** Clears the history of recently modified libraries. */
export function clearRecentlyModified() {
  return {
    type: CLEAR_RECENTLY_MODIFIED
  };
}
export const isClearRecentlyModified = isAction<ClearRecentlyModified>(CLEAR_RECENTLY_MODIFIED);
